import { Injectable } from '@angular/core';
import { Department } from '@app/models/department';
import { DepartmentService } from '@app/services/api/department.service';
import { FlashService } from '@app/services/flash.service';
import {
	// LoadCalculated,
	// LoadCalculatedFail,
	// LoadCalculatedSuccess,
	Create,
	CreateFail,
	CreateSuccess,
	Delete,
	DeleteFail,
	DeleteSuccess,
	DepartmentActionTypes,
	Download,
	DownloadFail,
	DownloadSuccess,
	LoadByConditions,
	LoadByConditionsFail,
	LoadByConditionsSuccess,
	LoadById,
	LoadByIdFail,
	LoadByIdSuccess,
	LoadFail,
	LoadSuccess,
	Search,
	SearchFail,
	SearchSuccess,
	Update,
	UpdateFail,
	UpdateSuccess,
	Upload,
	UploadFail,
	UploadLink,
	UploadLinkFail,
	UploadLinkSuccess,
	UploadProcessUpdate,
	Void,
	updateHierarchyPlannerFail,
	updateHierarchyPlannerSuccess
} from '@core/actions/department.actions';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { UploadFileFail } from '../actions/color.actions';

@Injectable()
export class DepartmentEffects {
	@Effect()
	load$: Observable<Action> = this.actions$.pipe(
		ofType(DepartmentActionTypes.Load),
		switchMap(() =>
			this.departmentsApi.get().pipe(
				map((departments: Department[]) => new LoadSuccess(departments)),
				catchError(error => of(new LoadFail(error)))
			)
		)
	);

	@Effect()
  upload$: Observable<Action> = this.actions$.pipe(
    ofType<Upload>(DepartmentActionTypes.Upload),
    map(action => [action.file, action.departmentId, action.upsertType]),
    switchMap(([file, departmentId, upsertType]) => {
      return this.departmentsApi.getDepartmentImportLink(file, departmentId, upsertType).pipe(
        map((record: any) => {
          return new UploadLink(record.signedUrl, record.id, file, upsertType);
        }),
        catchError(e => of(new UploadFail()))
      );
    })
  );

	@Effect()
  uploadLink$: Observable<Action> = this.actions$.pipe(
    ofType<UploadLink>(DepartmentActionTypes.UploadLink),
    map(action => [action.uploadUrl, action.FileId, action.file, action.upsertType]),
    switchMap(([uploadUrl, FileId, file, upsertType]) => {
      return this.departmentsApi.uploadFile(uploadUrl, file).pipe(
        map(record => {
          return new UploadLinkSuccess(FileId, upsertType);
        }),
        catchError(e => of(new UploadLinkFail(e)))
      );
    })
  );

  @Effect({dispatch: false})
  uploadLinkSuccess$ = this.actions$.pipe(
    ofType<UploadLinkSuccess>(DepartmentActionTypes.UploadLinkSuccess),
    map(action => [action.FileId, action.upsertType]),
    switchMap(([FileId, upsertType]) => {
      return this.departmentsApi
        .notifyFileUpload(FileId, { processType: upsertType })
        .pipe(
          map(record => {
            return new UploadProcessUpdate({
              status: 'pending',
              message: 'Procesando Packaging...'
            });
          }),
          catchError(e => of(new UploadFileFail(e)))
        );
    })
  );

	@Effect({dispatch: false})
  uploadFileFail$ = this.actions$.pipe(
    ofType<UploadFileFail>(DepartmentActionTypes.UploadLinkFail),
    map((action) => {
        this.fs.emit({ type: 'danger', message: 'No se pudo cargar' });
    })
  );
	
	@Effect()
	updateHierarchyPlanner$: Observable<Action> = this.actions$.pipe(
		ofType(DepartmentActionTypes.updateHierarchyPlanner),
		switchMap(() =>
			this.departmentsApi.updateHierarchy().pipe(
				map((departments: any) => new updateHierarchyPlannerSuccess()),
				catchError(error => of(new updateHierarchyPlannerFail(error)))
			)
		)
	);

	@Effect({ dispatch: false })
	updateHierarchyPlannerSuccess$ = this.actions$.pipe(
    	ofType<updateHierarchyPlannerSuccess>(DepartmentActionTypes.updateHierarchyPlannerSuccess),
    	map(action => action.data),
		tap(res =>
			this.fs.emit({
				message: 'Jerarquías sincronizadas con éxito!',
				type: 'success'
			})
		)
  	);

	@Effect({ dispatch: false })
	updateHierarchyPlannerFail$ = this.actions$.pipe(
		ofType<updateHierarchyPlannerFail>(DepartmentActionTypes.updateHierarchyPlannerFail),
		map(action => action.error),
		tap(res =>
			this.fs.emit({
				message: 'No se pudo sincronizar las Jerarquías',
				type: 'danger'
			})
		)
	);

	@Effect()
  download$ = this.actions$.pipe(
    ofType<Download>(DepartmentActionTypes.Download),
		switchMap(action => {
			return this.departmentsApi.downloadDep(action.departmentId).pipe(
					map((packaging: any) => {
							return new DownloadSuccess()
					}),
					catchError(error => of(new DownloadFail(error)))
			)
		})
  );

	// @Effect()
	// loadCalculated$: Observable<Action> = this.actions$.pipe(
	//   ofType(DepartmentActionTypes.LoadCalculated),
	//   switchMap(action =>
	//     this.departmentsApi.getCal(action).pipe(
	//       map(
	//         (departments: Department[]) => new LoadCalculatedSuccess(departments)
	//       ),
	//       catchError(error => of(new LoadCalculatedFail(error)))
	//     )
	//   )
	// );

	@Effect()
	loadByConditions: Observable<Action> = this.actions$.pipe(
		ofType<LoadByConditions>(DepartmentActionTypes.LoadByConditions),
		map(action => [action.FollowId, action.dataConditions]),
		switchMap(([FollowId, dataConditions]) =>
			this.departmentsApi.getByConditions(FollowId, dataConditions).pipe(
				map(
					(Departments: Department[]) =>
						new LoadByConditionsSuccess(Departments)
				),
				catchError(error => of(new LoadByConditionsFail(error)))
			)
		)
	);

	@Effect()
	create$: Observable<Action> = this.actions$.pipe(
		ofType<Create>(DepartmentActionTypes.Create),
		map(action => action.data),
		switchMap(data =>
			this.departmentsApi.create(data).pipe(
				map((department: Department) => new CreateSuccess(department)),
				catchError(error => of(new CreateFail(error)))
			)
		)
	);

	@Effect()
	createSuccess$: Observable<Action> = this.actions$.pipe(
		ofType<CreateSuccess>(DepartmentActionTypes.CreateSuccess),
		map(action => {
			this.fs.emit({ type: 'success', message: 'JP creado con exito' });
			return new Void()
		})
	);
	@Effect()
	search$: Observable<Action> = this.actions$.pipe(
		ofType<Search>(DepartmentActionTypes.Search),
		map(action => [action.data, action.FollowId]),
		switchMap(([data, FollowId]) =>
			this.departmentsApi.search(data, FollowId).pipe(
				map(
					(countryDepartmentsIds: any[]) =>
						new SearchSuccess(countryDepartmentsIds)
				),
				catchError(error => of(new SearchFail(error)))
			)
		)
	);

	@Effect()
	update$: Observable<Action> = this.actions$.pipe(
		ofType<Update>(DepartmentActionTypes.Update),
		map(action => [action.id, action.department]),
		concatMap(([id, department]) =>
			this.departmentsApi.update(id, department).pipe(
				map((department: Department) => { return new UpdateSuccess(id, department) }),
				catchError(error => of(new UpdateFail(error)))
			)
		)
	);

	@Effect()
	updateSuccess$: Observable<Action> = this.actions$.pipe(
		ofType<UpdateSuccess>(DepartmentActionTypes.UpdateSuccess),
		map(action => {
			this.fs.emit({ type: 'success', message: 'JP actualizado con exito' });
			return new Void()
		})
	);

	@Effect()
	updateFail$ = this.actions$.pipe(
		ofType<UpdateFail>(DepartmentActionTypes.UpdateFail),
		map(action => {
			this.fs.emit({ type: 'danger', message: 'Error al actualizar' });

			return new Void();
		})
	);



	@Effect()
	delete$: Observable<Action> = this.actions$.pipe(
		ofType<Delete>(DepartmentActionTypes.Delete),
		map(action => action.id),
		concatMap(id =>
			this.departmentsApi.destroySingle(id).pipe(
				concatMap(res => {
					this.fs.emit({
						type: 'success',
						message: 'JP eliminado exitosamente'
					});
					return [new DeleteSuccess(id)];
				}),
				catchError(error => of(new DeleteFail(error)))
			)
		)
	);

	@Effect()
	loadById$: Observable<Action> = this.actions$.pipe(
		ofType<LoadById>(DepartmentActionTypes.LoadById),
		map(action => [action.FollowId, action.dataFilter]),
		switchMap(([FollowId, dataFilter]) =>
			this.departmentsApi.getById(FollowId, dataFilter).pipe(
				map(
					(departments: Department[]) =>
						new LoadByIdSuccess(departments[0].id, {
							quantity: departments[0].quantity,
							totalUs: departments[0].totalUs,
						})
				),
				catchError(error => of(new LoadByIdFail(error)))
			)
		)
	);



	@Effect()
	deleteFail$: Observable<Action> = this.actions$.pipe(
		ofType<DeleteFail>(DepartmentActionTypes.DeleteFail),
		map(action => {
			// console.log(action)
			this.fs.emit({ type: 'danger', message: 'Error al eliminar' });
			return new Void()
		})
	);

	constructor(
		private actions$: Actions,
		private departmentsApi: DepartmentService,
		private fs: FlashService
	) { }
}
